// Terms & Conditions — booking-platform standard clauses + Travelsate-specific
// language around EMI mandates, cancellations, and the markups disclosed in the
// price breakdown.
import { Link } from "react-router-dom";

const LAST_UPDATED = "16 June 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="font-serif text-2xl text-[#D4AF37]">Travelsate</Link>
          <Link to="/" className="text-white/60 hover:text-[#D4AF37] text-sm">← Home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12" data-testid="terms-page">
        <h1 className="font-serif text-4xl text-[#D4AF37] mb-2">Terms &amp; Conditions</h1>
        <p className="text-white/40 text-xs mb-10">Last updated: {LAST_UPDATED}</p>

        <Section title="1. Acceptance">
          By creating an account or making a booking on travelsate.in you agree
          to these Terms, our Privacy Policy, and our Refund / Cancellation Policy.
          If you don't agree, please don't use the service.
        </Section>

        <Section title="2. Eligibility">
          You must be at least 18 years old and have legal capacity to contract.
          Group bookings: each adult traveller must consent individually before
          travel; the booking initiator warrants they have such consent.
        </Section>

        <Section title="3. Bookings & pricing">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>Prices shown are inclusive of all platform service charges. 18% GST is applied on top per Indian tax law.</li>
            <li>Live flight/hotel rates are sourced via SerpAPI at the time of booking and locked in once the upfront 1% (or full payment) is captured.</li>
            <li>Availability is live — packages may go unavailable between selection and payment. If a tier becomes unavailable post-selection, you'll be prompted to choose an alternative.</li>
          </ul>
        </Section>

        <Section title="4. Payment plans">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li><strong>Full Payment</strong>: charged immediately via Razorpay Checkout.</li>
            <li><strong>EMI plans</strong> (Weekly / Bi-weekly / Monthly): 1% upfront, with the balance auto-debited per the agreed schedule via Razorpay Subscriptions. You must authorise the auto-debit mandate (UPI AutoPay / Card / eNACH) within the same booking session — bookings without an authorised mandate are auto-cancelled after 7 days.</li>
            <li>Razorpay handles the actual debit attempts. If a debit fails, Razorpay retries per its own policy; persistent failures place the subscription on hold and may forfeit your booking.</li>
            <li>EMI plans require a pre-travel buffer of 15 days (domestic) or 30 days (international) so all installments clear before departure.</li>
          </ul>
        </Section>

        <Section title="5. Cancellation, reschedule & pause">
          See the <Link to="/refund-policy" className="text-[#D4AF37] underline">Refund / Cancellation Policy</Link>. In summary: cancellation fees scale with proximity to travel date, EMI payments made so far are credited toward the refund, and you may pause EMI for one cycle once per booking lifecycle.
        </Section>

        <Section title="6. Travel documents & responsibility">
          You are solely responsible for valid passports, visas, vaccinations,
          and insurance. Travelsate is not liable for denial of boarding, immigration
          refusal, or any cost arising from missing/invalid documents.
        </Section>

        <Section title="7. Conduct & abuse">
          We reserve the right to refuse service, cancel bookings, and pursue
          legal remedies in case of fraud, OTP abuse, payment-instrument fraud,
          chargebacks not preceded by a refund request, or harassment of our staff.
        </Section>

        <Section title="8. Liability cap">
          Total liability for any booking is capped at the actual amount paid by
          you for that booking. We are not liable for indirect or consequential
          losses, including lost holiday enjoyment.
        </Section>

        <Section title="9. Governing law">
          These Terms are governed by the laws of India. Disputes will be settled
          exclusively by the courts of Bengaluru, Karnataka.
        </Section>

        <Section title="10. Contact">
          Questions: <a href="mailto:admin@travelsate.in" className="text-[#D4AF37] underline">admin@travelsate.in</a> · <a href="tel:+919972744308" className="text-[#D4AF37] underline">+91 99727 44308</a>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-serif text-xl text-white mb-3">{title}</h2>
      <div className="text-white/70 text-sm leading-relaxed">{children}</div>
    </section>
  );
}
